/* eslint-disable */
// Pricing.js — Public pricing page at /pricing
// Route: <Route path="/pricing" element={<Pricing />} />
//
// PRICE-005: rebuilt on let-units, not properties. Free and Solo are HARD
// capped at 4 units. Standard, Pro and Agency are SOFT capped — the price
// shown is the base fee; going over the included units adds the per-unit
// overage rate automatically rather than blocking anything. See PLANS in
// server.js — this file has no shared source of truth with it, so a future
// price or allowance change needs updating by hand in both places.
//
// Two feature claims removed in this pass, unverified against the current
// codebase at the time: "Agent statements" — no matching module found,
// still unconfirmed. "MTD quarterly reports" was also removed, then
// restored once confirmed real (Reports.js has a genuine MTD Quarterly
// Reports export) — but reframed as an export, not a filing capability,
// since the report itself states it isn't linked to HMRC submission.
// "Rent roll" was also confirmed real and restored as its own row.

import { API_URL } from '../../config';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PLANS = [
    {
        key: 'free', name: 'Free', monthlyPrice: 0, annualPrice: 0,
        color: '#6b7280', units: '4 let units', cta: 'Get Started Free',
        features: [
            { label: '4 let units included',           included: true  },
            { label: '1 user',                          included: true  },
            { label: 'Rent & payment tracking',         included: true  },
            { label: 'Expense management',              included: true  },
            { label: 'Company expenses',                included: true  },
            { label: 'Tenant management',               included: true  },
            { label: 'Dashboard & overview',             included: true  },
            { label: 'Compliance calendar & alerts',     included: true  },
            { label: 'Data export (Excel)',              included: true  },
            { label: 'Reports & tax export',             included: false },
            { label: 'AI receipt & document scanning',   included: false },
            { label: 'Appraisal & deal pipeline',        included: false },
            { label: 'Service requests',                 included: false },
        ],
    },
    {
        key: 'solo', name: 'Solo', monthlyPrice: 8.00, annualPrice: 80.00,
        color: '#0891b2', units: '4 let units', cta: 'Start 14-day Free Trial',
        features: [
            { label: '4 let units included',            included: true  },
            { label: '1 user',                           included: true  },
            { label: 'Everything in Free',               included: true  },
            { label: 'Reports & tax export',             included: true  },
            { label: 'AI receipt & document scanning',   included: true  },
            { label: 'Appraisal & deal pipeline',        included: true  },
            { label: 'Service requests',                 included: false },
            { label: 'Multiple users',                   included: false },
        ],
    },
    {
        key: 'standard', name: 'Standard', monthlyPrice: 17.50, annualPrice: 175.00,
        color: '#2563eb', units: '24 let units', cta: 'Start 14-day Free Trial',
        features: [
            { label: '24 let units included',            included: true  },
            { label: 'Extra units at £1.00/mo each',      included: true  },
            { label: '3 users',                           included: true  },
            { label: 'Everything in Solo',                included: true  },
            { label: 'Service requests',                  included: false },
        ],
    },
    {
        key: 'pro', name: 'Pro', monthlyPrice: 35.00, annualPrice: 350.00,
        color: '#7c3aed', units: '60 let units', popular: true, cta: 'Start 14-day Free Trial',
        features: [
            { label: '60 let units included',             included: true },
            { label: 'Extra units at £0.80/mo (50p from unit 91)', included: true },
            { label: '10 users',                           included: true },
            { label: 'Everything in Standard',             included: true },
            { label: 'Refurbishment projects',             included: true },
            { label: 'Service requests',                   included: true },
        ],
    },
    {
        key: 'agency', name: 'Agency', monthlyPrice: null, annualPrice: null,
        color: '#065f46', units: '150 let units', cta: 'Contact Us',
        features: [
            { label: '150 let units included',            included: true },
            { label: 'Extra units at £0.50/mo each',       included: true },
            { label: 'Users to suit your team',            included: true },
            { label: 'Everything in Pro',                  included: true },
            { label: 'Guided setup & data import',         included: true },
            { label: 'Priority support',                   included: true },
            { label: 'Dedicated onboarding',                included: true },
        ],
    },

];

// ── Comparison table data ──────────────────────────────────────────
const COMPARISON_PLANS = [
    { name: 'Free',     price: '£0',        color: '#6b7280' },
    { name: 'Solo',     price: '£8.00/mo',  color: '#0891b2' },
    { name: 'Standard', price: '£17.50/mo', color: '#2563eb' },
    { name: 'Pro',      price: '£35.00/mo', color: '#7c3aed' },
    { name: 'Agency',   price: 'Custom',    color: '#065f46' },
];
const COMPARISON_COLSPAN = COMPARISON_PLANS.length + 1; // +1 for the label column

const COMPARISON_ROWS = [
    { heading: 'Limits' },
    { label: 'Let units included',      values: ['4', '4', '24', '60', '150'] },
    { label: 'Users',                   values: ['1', '1', '3', '10', 'Custom'] },
    { label: 'Extra units',             values: [false, false, '£1.00/mo', '£0.80/mo (50p 91+)', '£0.50/mo'] },

    { heading: 'Core' },
    { label: 'Dashboard & overview',    values: [true, true, true, true, true] },
    { label: 'Property management',     values: [true, true, true, true, true] },
    { label: 'Tenant management',       values: [true, true, true, true, true] },
    { label: 'Rent & payment tracking', values: [true, true, true, true, true] },
    { label: 'Expense management',      values: [true, true, true, true, true] },
    { label: 'Company expenses',        values: [true, true, true, true, true] },

    { heading: 'Reporting' },
    { label: 'Data export (Excel)',     values: [true, true, true, true, true] },
    { label: 'Export date ranges',      values: [true, true, true, true, true] },
    { label: 'Profit & loss reports',   values: [false, true, true, true, true] },
    { label: 'HMRC tax reports',        values: [false, true, true, true, true] },
    { label: 'Arrears & void reports',  values: [false, true, true, true, true] },
    { label: 'Rent roll',               values: [false, true, true, true, true] },
    { label: 'MTD-format quarterly reports', values: [false, true, true, true, true] },

    { heading: 'Operations' },
    { label: 'AI receipt & document scanning', values: [false, true, true, true, true] },
    { label: 'Compliance calendar & alerts',   values: [true, true, true, true, true] },
    { label: 'Appraisal & deal pipeline',      values: [false, true, true, true, true] },
    { label: 'Refurbishment projects',         values: [false, false, false, true, true] },
    { label: 'Service requests',               values: [false, false, false, true, true] },

    { heading: 'Support' },
    { label: 'Email support',           values: [true, true, true, true, true] },
    { label: 'Priority support',        values: [false, false, false, true, true] },
    { label: 'Dedicated onboarding',    values: [false, false, false, false, true] },
];

export default function Pricing() {
    const [interval, setInterval]       = useState('month');
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [error, setError]             = useState('');
    const navigate = useNavigate();

    const handleCTA = async (plan) => {
        if (plan.key === 'free')   { navigate('/login'); return; }
        if (plan.key === 'agency') { navigate('/contact'); return; }
        // BUG FIX: a visitor who isn't logged in yet had no token, so this
        // sent "Authorization: Bearer null" straight to the backend, which
        // correctly rejected it — but the visitor just saw a generic
        // "Checkout failed" error with no indication that registering
        // first was the actual missing step. Routes them into registration
        // with the plan they picked already pre-selected instead (reusing
        // Login.js's own plan-selection + checkout flow), so clicking a
        // paid plan here leads straight toward actually paying for it,
        // not a dead end.
        const token = localStorage.getItem('token');
        if (!token) {
            navigate(`/login?signup=1&plan=${plan.key}&interval=${interval}`);
            return;
        }
        setLoadingPlan(plan.key);
        setError('');
        try {
            const BASE  = API_URL;
            const res   = await fetch(`${BASE}/api/billing/create-checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ planName: plan.key, interval }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Checkout failed');
            window.location.href = data.url;
        } catch (e) {
            setError(e.message || 'Something went wrong. Please try again.');
            setLoadingPlan(null);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'Inter, system-ui, sans-serif' }}>
            {/* Header */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
                    <img src="/casavello-icon-500.png" alt="" style={{ height: 32, width: 32, objectFit: 'contain' }} />
                    <span style={{ fontWeight: 800, fontSize: '1.25rem', color: '#166534' }}>casavello</span>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button onClick={() => navigate('/login')} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 600, color: '#374151' }}>Log in</button>
                    <button onClick={() => navigate('/login')} style={{ background: '#166534', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 700 }}>Get started free</button>
                </div>
            </div>

            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '3rem 1.5rem' }}>
                {/* Hero */}
                <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                    <img src="/casavello-stacked.png" alt="casavello" style={{ width: 150, height: 'auto', objectFit: 'contain', marginBottom: '1rem' }} />
                    <h1 style={{ fontSize: '2.25rem', fontWeight: 800, color: '#111', marginBottom: '0.5rem' }}>
                        Simple pricing for every portfolio size
                    </h1>
                    <p style={{ fontSize: '1rem', color: '#166534', fontWeight: 600, marginBottom: '0.75rem' }}>
                        Your entire property portfolio, effortlessly managed
                    </p>
                    <p style={{ fontSize: '1.0625rem', color: '#6b7280', maxWidth: 560, margin: '0 auto' }}>
                        Priced on let units, not properties — a single let is 1 unit, a six-bed HMO is 6.
                        Start free with 4 units. No credit card required.
                    </p>
                </div>

                {/* Billing toggle */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', background: '#e5e7eb', borderRadius: '0.625rem', padding: '0.25rem' }}>
                        {[['month','Monthly'],['year','Annual — 2 months free 🎉']].map(([key, label]) => (
                            <button key={key} onClick={() => setInterval(key)} style={{
                                padding: '0.5rem 1.25rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
                                fontWeight: 600, fontSize: '0.9rem',
                                background: interval === key ? '#fff' : 'transparent',
                                color:      interval === key ? '#111' : '#6b7280',
                                boxShadow:  interval === key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                                transition: 'all 0.15s',
                            }}>{label}</button>
                        ))}
                    </div>
                </div>

                {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem 1rem', color: '#dc2626', textAlign: 'center', marginBottom: '1.5rem' }}>{error}</div>}

                {/* Plan cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1.25rem', alignItems: 'start' }}>
                    {PLANS.map(plan => {
                        const price    = plan.monthlyPrice === null ? 'Custom' : interval === 'year' && plan.annualPrice > 0 ? `£${(plan.annualPrice / 12).toFixed(2)}` : plan.monthlyPrice === 0 ? 'Free' : `£${plan.monthlyPrice.toFixed(2)}`;
                        const isLoading = loadingPlan === plan.key;
                        return (
                            <div key={plan.key} style={{
                                background: '#fff', borderRadius: '1rem', padding: '1.75rem',
                                border:     plan.popular ? `2px solid ${plan.color}` : '1px solid #e5e7eb',
                                position:   'relative',
                                boxShadow:  plan.popular ? `0 4px 20px ${plan.color}25` : '0 1px 4px rgba(0,0,0,0.06)',
                            }}>
                                {plan.popular && (
                                    <div style={{ position: 'absolute', top: '-0.75rem', left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#fff', borderRadius: '1rem', padding: '0.25rem 0.875rem', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        Most Popular
                                    </div>
                                )}
                                <div style={{ fontWeight: 700, fontSize: '1rem', color: plan.color, marginBottom: '0.125rem' }}>{plan.name}</div>
                                <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.75rem' }}>{plan.units}</div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <span style={{ fontSize: '2rem', fontWeight: 800, color: '#111' }}>{price}</span>
                                    {plan.monthlyPrice > 0 && <span style={{ fontSize: '1rem', color: '#6b7280' }}>/mo</span>}
                                    {plan.monthlyPrice === null && <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Tailored to your portfolio</div>}
                                    {interval === 'year' && plan.annualPrice > 0 && (
                                        <div style={{ fontSize: '0.8125rem', color: '#059669', fontWeight: 600 }}>£{plan.annualPrice.toFixed(2)} billed annually</div>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleCTA(plan)} disabled={!!loadingPlan}
                                    style={{
                                        width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: 'none', marginBottom: '1.25rem',
                                        background: plan.monthlyPrice === 0 ? '#f3f4f6' : plan.color,
                                        color:      plan.monthlyPrice === 0 ? '#374151' : '#fff',
                                        fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer',
                                        opacity: loadingPlan && !isLoading ? 0.6 : 1,
                                    }}
                                >{isLoading ? 'Redirecting...' : plan.cta}</button>
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {plan.features.map(f => (
                                        <li key={f.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: f.included ? '#374151' : '#9ca3af' }}>
                                            <span>{f.included ? '✅' : '⬜'}</span>{f.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>

                {/* ── Full feature comparison ─────────────────────────── */}
                <div style={{ marginTop: '3.5rem' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111', textAlign: 'center', marginBottom: '0.5rem' }}>
                        Compare every plan
                    </h2>
                    <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '1.75rem', fontSize: '0.9375rem' }}>
                        Everything that's included, side by side.
                    </p>

                    <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '1rem', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ textAlign: 'left', padding: '1rem 1.25rem', fontSize: '0.8125rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Feature</th>
                                    {COMPARISON_PLANS.map(p => (
                                        <th key={p.name} style={{ padding: '1rem 0.75rem', textAlign: 'center', minWidth: 100 }}>
                                            <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: p.color }}>{p.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>{p.price}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARISON_ROWS.map((row, i) => (
                                    row.heading ? (
                                        <tr key={i} style={{ background: '#f9fafb' }}>
                                            <td colSpan={COMPARISON_COLSPAN} style={{ padding: '0.6rem 1.25rem', fontSize: '0.75rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                {row.heading}
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '0.7rem 1.25rem', fontSize: '0.875rem', color: '#374151' }}>{row.label}</td>
                                            {row.values.map((v, j) => (
                                                <td key={j} style={{ padding: '0.7rem 0.75rem', textAlign: 'center', fontSize: '0.875rem', color: v === false ? '#d1d5db' : '#374151', fontWeight: typeof v === 'string' ? 600 : 400 }}>
                                                    {v === true ? '✅' : v === false ? '—' : v}
                                                </td>
                                            ))}
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                        <button onClick={() => navigate('/contact')} style={{ background: '#065f46', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.75rem 1.5rem', fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer' }}>
                            Outgrown 150 let units? Talk to us →
                        </button>
                    </div>
                    <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem', maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
                        MTD-format quarterly reports are produced for your own records and for your accountant — casavello does not submit anything to HMRC on your behalf. Not tax advice.
                    </p>
                </div>

                <div style={{ textAlign: 'center', marginTop: '2.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    <p>All prices exclude VAT · 14-day free trial on all paid plans · No card required to start · Cancel anytime</p>
                    <p style={{ marginTop: '0.25rem' }}>Questions? <a href="mailto:info@casavello.io" style={{ color: '#166534' }}>info@casavello.io</a></p>
                </div>
            </div>
        </div>
    );
}
